/**
 * The application shell — tasks P2-05 and P3-06.
 *
 * Three states, in order of precedence: signed out, signed in but unverified,
 * and signed in. The unverified state is its own screen rather than a banner
 * because there is exactly one useful action in it, and burying that under a
 * device list the user cannot populate is worse than showing nothing else.
 *
 * Routing is a hash fragment read directly, not a router library. There are
 * four screens, one of which exists only to receive a link from an email; a
 * router would be more configuration than routing.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { getMe, listDevices, type DeviceSummary, type Me } from "./api.js";
import { authClient, useSession } from "./auth.js";
import { VoltageChart, type ChartRange } from "./components/VoltageChart.js";
import {
    ClaimDeviceForm,
    DeviceSettings,
    MembersPanel,
} from "./pages/Account.js";
import { AuthPage, ResetPasswordPage, type AuthMode } from "./pages/Auth.js";
import { useChartData } from "./useChartData.js";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const PRESETS: { label: string; span: number }[] = [
    { label: "6h", span: 6 * HOUR },
    { label: "Day", span: DAY },
    { label: "Week", span: 7 * DAY },
    { label: "Month", span: 30 * DAY },
    { label: "Year", span: 365 * DAY },
];

const TIER_LABEL: Record<string, string> = {
    raw: "raw entries",
    rollup_5m: "5-minute buckets",
    rollup_1h: "hourly buckets",
    rollup_1d: "daily buckets",
};

type Screen = "chart" | "devices" | "account";

/** `#/reset-password?token=…` arrives from an email and is handled first. */
function readHash(): { screen: Screen; resetToken: string | null } {
    const hash = window.location.hash.replace(/^#\/?/, "");
    const [path, query] = hash.split("?");
    const token = new URLSearchParams(query ?? "").get("token");

    if (path === "reset-password" && token) {
        return { screen: "chart", resetToken: token };
    }
    return {
        screen: path === "devices" || path === "account" ? path : "chart",
        resetToken: null,
    };
}

export function App() {
    const { data: session, isPending } = useSession();
    const [route, setRoute] = useState(readHash);
    const [authMode, setAuthMode] = useState<AuthMode>("sign-in");

    useEffect(() => {
        const onHashChange = () => setRoute(readHash());
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);

    if (route.resetToken) {
        return (
            <main className="app centered">
                <ResetPasswordPage
                    token={route.resetToken}
                    onDone={() => {
                        window.location.hash = "";
                        setRoute({ screen: "chart", resetToken: null });
                        setAuthMode("sign-in");
                    }}
                />
            </main>
        );
    }

    if (isPending) {
        return (
            <main className="app centered">
                <p className="note">Loading…</p>
            </main>
        );
    }

    if (!session) {
        return (
            <main className="app centered">
                <AuthPage mode={authMode} onModeChange={setAuthMode} />
            </main>
        );
    }

    if (!session.user.emailVerified) {
        return (
            <main className="app centered">
                <UnverifiedNotice email={session.user.email} />
            </main>
        );
    }

    return <SignedIn screen={route.screen} onNavigate={setRoute} />;
}

function UnverifiedNotice({ email }: { email: string }) {
    const [status, setStatus] = useState<string | null>(null);

    return (
        <div className="auth-card">
            <h1>Confirm your email</h1>
            <p className="note">
                We sent a link to <strong>{email}</strong>. Confirming it is what
                lets you add a device — alerts about your vehicle go to this
                address, so it has to be one you control.
            </p>
            <button
                type="button"
                onClick={async () => {
                    await authClient.sendVerificationEmail({ email });
                    setStatus("Sent. Check your inbox.");
                }}
            >
                Send it again
            </button>
            {status ? <p className="note">{status}</p> : null}
            <button type="button" onClick={() => void authClient.signOut()}>
                Sign out
            </button>
        </div>
    );
}

function SignedIn({ screen, onNavigate }: {
    screen: Screen;
    onNavigate: (route: { screen: Screen; resetToken: null }) => void;
}) {
    const [me, setMe] = useState<Me | null>(null);
    const [devices, setDevices] = useState<DeviceSummary[]>([]);
    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [range, setRange] = useState<ChartRange>(() => {
        const now = Date.now();
        return { from: now - DAY, to: now };
    });

    const refresh = useCallback(async () => {
        try {
            const [profile, deviceList] = await Promise.all([getMe(), listDevices()]);
            setMe(profile);
            setDevices(deviceList.devices);

            setDeviceId((current) => {
                if (current && deviceList.devices.some((d) => d.deviceId === current)) {
                    return current;
                }
                return deviceList.devices[0]?.deviceId ?? null;
            });
        } catch (caught) {
            setError(String(caught));
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const selected = useMemo(
        () => devices.find((device) => device.deviceId === deviceId) ?? null,
        [devices, deviceId],
    );

    // Open on a range that actually has data. Defaulting to "the last 24 hours"
    // shows an empty chart for any device that has been offline, which reads as
    // a broken app rather than as a device that has not reported.
    useEffect(() => {
        if (selected?.dataTo != null && selected.dataFrom != null) {
            setRange({
                from: Math.max(selected.dataFrom, selected.dataTo - DAY),
                to: selected.dataTo,
            });
        }
    }, [selected?.deviceId, selected?.dataFrom, selected?.dataTo]);

    const { data, loading, error: chartError } = useChartData(deviceId, range);

    const applyPreset = useCallback(
        (span: number) => {
            const end = selected?.dataTo ?? Date.now();
            setRange({ from: end - span, to: end });
        },
        [selected],
    );

    const go = (next: Screen) => {
        window.location.hash = next === "chart" ? "" : `#/${next}`;
        onNavigate({ screen: next, resetToken: null });
    };

    return (
        <main className="app">
            <header className="app-header">
                <h1>ChargeHawk</h1>

                <nav className="tabs">
                    {(["chart", "devices", "account"] as const).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            className={screen === tab ? "active" : ""}
                            onClick={() => go(tab)}
                        >
                            {tab === "chart" ? "Chart" : tab === "devices" ? "Devices" : "Account"}
                        </button>
                    ))}
                </nav>

                <div className="spacer" />
                <span className="who">{me?.user.email}</span>
                <button type="button" onClick={() => void authClient.signOut()}>
                    Sign out
                </button>
            </header>

            {error ? <p className="error">{error}</p> : null}

            {screen === "chart" ? (
                devices.length === 0 ? (
                    <section className="panel">
                        <h2>No devices yet</h2>
                        <p className="note">
                            Add one from the Devices tab using the ID and claim code
                            on its label.
                        </p>
                        <button type="button" onClick={() => go("devices")}>
                            Add a device
                        </button>
                    </section>
                ) : (
                    <>
                        <section className="controls">
                            <label className="device-picker">
                                <span>Device</span>
                                <select
                                    value={deviceId ?? ""}
                                    onChange={(event) => setDeviceId(event.target.value)}
                                >
                                    {devices.map((device) => (
                                        <option key={device.deviceId} value={device.deviceId}>
                                            {device.chId}
                                            {device.dataTo === null ? " (no data)" : ""}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <div className="presets" role="group" aria-label="Time range">
                                {PRESETS.map((preset) => (
                                    <button
                                        key={preset.label}
                                        type="button"
                                        onClick={() => applyPreset(preset.span)}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                        </section>

                        {chartError ? <p className="error">{chartError}</p> : null}

                        <section className="status">
                            <span>
                                {new Date(range.from).toLocaleString()} –{" "}
                                {new Date(range.to).toLocaleString()}
                            </span>
                            {data ? (
                                <>
                                    <span
                                        className="tier"
                                        title="Chosen server-side from the range"
                                    >
                                        {TIER_LABEL[data.tier] ?? data.tier}
                                    </span>
                                    <span>{data.points.length.toLocaleString()} points</span>
                                    {data.unplaced.length > 0 ? (
                                        <span className="warning">
                                            {data.unplaced.length} region
                                            {data.unplaced.length === 1 ? "" : "s"} of
                                            unplaced data
                                        </span>
                                    ) : null}
                                </>
                            ) : null}
                        </section>

                        <VoltageChart
                            series={data?.points ?? []}
                            events={[]}
                            range={range}
                            onRangeChange={setRange}
                            unplaced={data?.unplaced ?? []}
                            loading={loading}
                        />

                        {data && data.unplaced.length > 0 ? (
                            <p className="note">
                                Shaded regions mark data the device recorded before its
                                clock was set. It is stored, but cannot yet be placed on
                                the timeline — the bounds shown are the segments recorded
                                either side of it.
                            </p>
                        ) : null}
                    </>
                )
            ) : null}

            {screen === "devices" && me ? (
                <>
                    <ClaimDeviceForm me={me} onClaimed={() => void refresh()} />
                    <section className="panel">
                        <h2>
                            Your devices ({me.deviceCount}/{me.plan.maxDevices})
                        </h2>
                        {devices.length === 0 ? (
                            <p className="note">Nothing claimed yet.</p>
                        ) : (
                            <ul className="device-list">
                                {devices.map((device) => (
                                    <li key={device.deviceId}>
                                        <strong>{device.chId}</strong>
                                        <span>{device.model ?? "unknown model"}</span>
                                        <span>
                                            {device.lastSeenAt
                                                ? `last seen ${new Date(device.lastSeenAt).toLocaleString()}`
                                                : "never reported"}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setDeviceId(device.deviceId);
                                                go("chart");
                                            }}
                                        >
                                            View chart
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    {selected ? (
                        <DeviceSettings device={selected} onChanged={() => void refresh()} />
                    ) : null}
                </>
            ) : null}

            {screen === "account" && me ? (
                <>
                    <section className="panel">
                        <h2>{me.account.name}</h2>
                        <dl className="facts">
                            <dt>Plan</dt>
                            <dd>
                                {me.plan.code} — {me.plan.maxDevices} device
                                {me.plan.maxDevices === 1 ? "" : "s"},{" "}
                                {me.plan.retentionDays} days of detailed history
                            </dd>
                            <dt>Your role</dt>
                            <dd>{me.account.role}</dd>
                            <dt>Devices</dt>
                            <dd>
                                {me.deviceCount} of {me.plan.maxDevices}
                            </dd>
                        </dl>
                    </section>
                    <MembersPanel me={me} />
                </>
            ) : null}
        </main>
    );
}
